import * as THREE from '/lib/three/three.module.min.js';   // REAL path, not the bare "three" specifier (import maps need Safari 16.4+; this must resolve on Safari 15.6)
import { blendColors, lightenColor, darkenColor, hash01, hexSeed, nameSeed } from './texture-helpers.js';
// The procedural material library. TextureEngine is the cache + dispatch; these are the surfaces it bakes.
import {
    createFloatGlassTexture, createWiredGlassTexture,
    createSiliconFractureTexture, createGraphitePlateTexture, createAlkaliTarnishTexture, createHaliteCleavageTexture,
    createPisoliticTexture, createDisseminatedOreTexture, createVesicularSlagTexture, createNanofoamTexture, createCorTexture,
    createGlacialIce, createDryIceFrost, createBladedFrost, createSugarFrost, createConvectionIce, createTholinCrust,
    createCircuitBoardTexture, createPhotovoltaicTexture, createPowerCoreTexture, createBatteryTexture, createArmorPlateTexture, createSilicaSandTexture,
    createGasVesselTexture, createCryoBoilTexture, createFluidFlowTexture
} from './texture-materials.js';
import { ORGANIC_STYLES } from './texture-organics.js';            // life's tissues: bark, leaves, petals, gills, coral… (the review's round 2)
import { METAL_SURFACES } from './texture-metals.js';                  // one surface per metal that is not a rectangle lattice
import { drawMineralCrust, drawRegolithCrust } from './texture-crust.js';   // the ground of every rocky world, THREE-free
import { BIOLITH_MAKERS } from './texture-biolith.js';                // the biolith kubes' nine masonry patterns (Oct 13), THREE-free
import { BUILD_MAKERS, BUILD_LABELS } from './texture-building.js';    // the building kubes' faces (Oct 13), THREE-free
import { biolithPattern } from './biolith.mjs';
import { kubeTextureType, craftedTextureKey, nativeTextureKey, biolithTextureKey, buildingTextureKey, BUILDING_WHOLE_FACE, isCraftSurfaceName, isCraftSurfaceKey, bitBaseName, metalnessFor, iconMetalness, CRAFTED_TEXTURES, CRAFT_SURFACE_KEYS, isGaugeKey, textureSlot, setLifeFamilyLookup} from './texture-dispatch.js';
import { KUBE_PICKS, LIFE_PICKS } from './texture-picks.mjs';          // the owner's picks from the Material Bench (Sep 30)
import { LOOK_TEXTURES } from './texture-looks.mjs';
import { bakeRecipe } from './texture-recipes.js';
import { ORGANIC_MATERIALS } from './organics.mjs';   // the name→texture decision, THREE-free so it is node-testable

// THE RECTANGLE LATTICES — Iron, Gold, Copper, Silver and four ores: a base of the kube's own colour scattered with
// rectangles in two tones blended from it. Every choice below is drawn from hash01 under a seed made from the
// lattice's NAME, never from Math.random. Rolling it at draw time gave Iron a different rust pattern every session
// and on every client, so two players standing on one Iron seam saw two different blocks. Keyed by name rather than
// hex because Gold and Sulfur share #FFD700, and each should still have its own layout. The base and the two tones
// are baked from the hex this slot is handed, so the canvas is a pure function of the name and the colour.
//
// A rectangle that runs off the right or bottom edge is drawn again on the far side. It used to be cut off at the
// edge, which left a seam at every kube boundary in a wall of any of these.
function createRandomRectangleLattice(baseHex, name, minSize, maxSize, minWidth, maxWidth, rectCount, secondaryColor1, secondaryColor2) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = nameSeed(name);

    ctx.fillStyle = baseHex;
    ctx.fillRect(0, 0, size, size);

    for (let i = 0; i < rectCount; i++) {
        const x = hash01(i + seed, 1) * size;
        const y = hash01(i + seed, 2) * size;
        const width = minWidth + hash01(i + seed, 3) * (maxWidth - minWidth);
        const height = minSize + hash01(i + seed, 4) * (maxSize - minSize);
        ctx.fillStyle = hash01(i, seed + 5) > 0.5 ? secondaryColor1 : secondaryColor2;
        const wx = x + width > size, wy = y + height > size;
        ctx.fillRect(x, y, width, height);
        if (wx) ctx.fillRect(x - size, y, width, height);
        if (wy) ctx.fillRect(x, y - size, width, height);
        if (wx && wy) ctx.fillRect(x - size, y - size, width, height);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

function createIronLattice(baseHex) {
    const rustColor = '#8B4513';
    const darkColor = '#333333';
    const secondary1 = blendColors(darkenColor(baseHex, 0.4), rustColor, 0.6);
    const secondary2 = blendColors(darkenColor(baseHex, 0.2), darkColor, 0.5);
    return createRandomRectangleLattice(baseHex, 'Iron', 40, 100, 50, 120, 80, secondary1, secondary2);
}

function createGoldLattice(baseHex) {
    const tarnishColor = '#1a1a1a';
    const shadowColor = '#8B6914';
    const secondary1 = blendColors(darkenColor(baseHex, 0.35), tarnishColor, 0.5);
    const secondary2 = blendColors(darkenColor(baseHex, 0.15), shadowColor, 0.6);
    return createRandomRectangleLattice(baseHex, 'Gold', 20, 60, 30, 80, 150, secondary1, secondary2);
}

function createCopperLattice(baseHex) {
    const verdigrisColor = '#5CB85C';
    // A DARK copper, for the shadowed pits — the way Iron blends toward #333333. This was #B87333, which is the
    // traditional copper hex and was Copper's OWN colour before the catalogue moved it to #9D7A54; blending the base
    // toward (a lighter version of) itself made half the rectangles invisible. It went unnoticed because blendColors
    // was silently producing an invalid colour for this call anyway, so those rectangles used to inherit the green
    // verdigris instead and the lattice looked varied for the wrong reason.
    const darkColor = '#4A2F14';
    const secondary1 = blendColors(darkenColor(baseHex, 0.15), verdigrisColor, 0.5);
    const secondary2 = blendColors(baseHex, darkColor, 0.4);
    return createRandomRectangleLattice(baseHex, 'Copper', 30, 80, 40, 100, 120, secondary1, secondary2);
}

function createSilverLattice(baseHex) {
    const tarnishColor = '#4A4A4A';
    const shadowColor = '#1a1a1a';
    const secondary1 = blendColors(darkenColor(baseHex, 0.15), tarnishColor, 0.7);
    const secondary2 = blendColors(darkenColor(baseHex, 0.25), shadowColor, 0.5);
    return createRandomRectangleLattice(baseHex, 'Silver', 15, 50, 20, 70, 200, secondary1, secondary2);
}

function createPyriteLattice(baseHex) {
    const darkerBrass = '#8B7D3D';
    const weatheredColor = '#6B5D2F';
    const secondary1 = blendColors(darkenColor(baseHex, 0.2), darkerBrass, 0.6);
    const secondary2 = blendColors(darkenColor(baseHex, 0.3), weatheredColor, 0.5);
    return createRandomRectangleLattice(baseHex, 'Pyrite', 25, 75, 35, 95, 100, secondary1, secondary2);
}

function createMagnetiteLattice(baseHex) {
    const lightGrey = '#B0B0B0';
    const mediumGrey = '#707070';
    const secondary1 = blendColors(baseHex, lightGrey, 0.6);
    const secondary2 = blendColors(baseHex, mediumGrey, 0.5);
    return createRandomRectangleLattice(baseHex, 'Magnetite', 20, 60, 30, 80, 110, secondary1, secondary2);
}

function createPhosphorusLattice(baseHex) {
    const oxidizedBrown = '#8B5A2B';
    const weatheredCream = '#F5DEB3';
    const secondary1 = blendColors(darkenColor(baseHex, 0.2), oxidizedBrown, 0.5);
    const secondary2 = blendColors(lightenColor(baseHex, 0.3), weatheredCream, 0.4);
    return createRandomRectangleLattice(baseHex, 'Phosphorus', 20, 60, 30, 80, 90, secondary1, secondary2);
}

function createSulfurLattice(baseHex) {
    const darkGold = '#CC9900';
    const brightWhite = '#FFFFFF';
    const secondary1 = blendColors(darkenColor(baseHex, 0.4), darkGold, 0.6);
    const secondary2 = blendColors(lightenColor(baseHex, 0.4), brightWhite, 0.5);
    return createRandomRectangleLattice(baseHex, 'Sulfur', 15, 55, 25, 75, 120, secondary1, secondary2);
}

function createCrystallineLattice(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const prismColor = '#E8F4FF';
    const cleftColor = '#141820';
    const facetLight = blendColors(lightenColor(baseHex, 0.3), prismColor, 0.35);
    const facetDark = blendColors(darkenColor(baseHex, 0.3), cleftColor, 0.45);
    const glintColor = blendColors(lightenColor(baseHex, 0.45), prismColor, 0.6);
    const seed = hexSeed(baseHex);

    ctx.fillStyle = baseHex;
    ctx.fillRect(0, 0, size, size);

    // A jittered point grid: every cell is filled as the quad between four neighbouring points, so no two facets share
    // an edge angle and the surface reads as cleaved crystal rather than tiles. Border points are pinned so the tile
    // still repeats. Jitter and per-facet shade both come from hash01, i.e. the same colour folds the same crystal.
    const cell = 64, span = size / cell;
    const px = [], py = [];
    for (let j = 0; j <= span; j++) {
        px.push([]); py.push([]);
        for (let i = 0; i <= span; i++) {
            const pinned = (i === 0 || j === 0 || i === span || j === span);
            px[j].push(i * cell + (pinned ? 0 : (hash01(i + seed, j) - 0.5) * cell * 0.7));
            py[j].push(j * cell + (pinned ? 0 : (hash01(i, j + seed) - 0.5) * cell * 0.7));
        }
    }

    for (let j = 0; j < span; j++) {
        for (let i = 0; i < span; i++) {
            const shade = hash01(i + seed, j + seed);
            ctx.globalAlpha = 0.25 + shade * 0.5;
            ctx.fillStyle = shade > 0.5 ? facetLight : facetDark;
            ctx.beginPath();
            ctx.moveTo(px[j][i], py[j][i]);
            ctx.lineTo(px[j][i + 1], py[j][i + 1]);
            ctx.lineTo(px[j + 1][i + 1], py[j + 1][i + 1]);
            ctx.lineTo(px[j + 1][i], py[j + 1][i]);
            ctx.closePath();
            ctx.fill();
        }
    }

    ctx.strokeStyle = glintColor;                                // cleavage glints along a noise-chosen third of the diagonals
    ctx.lineWidth = 2;
    for (let j = 0; j < span; j++) {
        for (let i = 0; i < span; i++) {
            if (hash01(i * 2 + seed, j * 3) < 0.68) continue;
            ctx.globalAlpha = 0.3 + hash01(i * 3, j * 2 + seed) * 0.45;
            ctx.beginPath();
            ctx.moveTo(px[j][i], py[j][i]);
            ctx.lineTo(px[j + 1][i + 1], py[j + 1][i + 1]);
            ctx.stroke();
        }
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

function createDamascusPattern(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const etchColor = '#F2EFE6';
    const carbonColor = '#2A2621';
    const brightSteel = blendColors(lightenColor(baseHex, 0.3), etchColor, 0.4);
    const darkSteel = blendColors(darkenColor(baseHex, 0.32), carbonColor, 0.5);
    const seed = hexSeed(baseHex);

    ctx.fillStyle = baseHex;
    ctx.fillRect(0, 0, size, size);

    // 110 folded layers. Each is a full-width polyline whose height is two summed sines with WHOLE-cycle frequencies,
    // so a band leaves the right edge exactly where it entered the left and the fold survives RepeatWrapping.
    // Amplitude, phase, thickness and light/dark side are all hash01(band, seed) reads — no draw-time randomness.
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const bands = 110;
    for (let b = 0; b < bands; b++) {
        const y0 = (b / bands) * size;
        const a1 = 18 + hash01(b + seed, 1) * 46;
        const a2 = 8 + hash01(b + seed, 2) * 22;
        const p1 = hash01(b + seed, 3) * Math.PI * 2;
        const p2 = hash01(b + seed, 4) * Math.PI * 2;
        const cycles = 1 + Math.floor(hash01(b + seed, 5) * 3);
        ctx.globalAlpha = 0.25 + hash01(b, seed + 1) * 0.45;
        ctx.strokeStyle = hash01(b, seed + 2) > 0.5 ? brightSteel : darkSteel;
        ctx.lineWidth = 3 + hash01(b, seed + 3) * 9;
        ctx.beginPath();
        for (let x = 0; x <= size; x += 16) {
            const t = (x / size) * Math.PI * 2;
            const y = y0 + Math.sin(t * cycles + p1) * a1 + Math.sin(t * cycles * 2 + p2) * a2;
            if (x === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }
        ctx.stroke();
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

function createEmissivePulseLattice(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const coreColor = '#FFFFFF';
    const shellColor = '#0A0A10';
    const conduitColor = blendColors(lightenColor(baseHex, 0.4), coreColor, 0.5);
    const shell = blendColors(darkenColor(baseHex, 0.5), shellColor, 0.55);
    const nodeColor = blendColors(lightenColor(baseHex, 0.55), coreColor, 0.7);
    const seed = hexSeed(baseHex);

    ctx.fillStyle = shell;
    ctx.fillRect(0, 0, size, size);

    // Conduit grid on a dark shell. Each lane is lit to its own brightness/width — a pulse frozen mid-travel — and the
    // junctions bloom on top. Lane spacing divides 1024 exactly so the grid still lines up across a repeated tile, and
    // every brightness is hash01(lane, seed): the same matrix lights the same lanes on every machine.
    const lanes = 8, gap = size / lanes;
    ctx.lineCap = 'square';
    ctx.strokeStyle = conduitColor;
    for (let i = 0; i < lanes; i++) {
        const h = hash01(i + seed, 31), v = hash01(i + seed, 32);
        ctx.globalAlpha = 0.15 + h * 0.7;
        ctx.lineWidth = 4 + h * 10;
        ctx.beginPath(); ctx.moveTo(0, i * gap + gap / 2); ctx.lineTo(size, i * gap + gap / 2); ctx.stroke();
        ctx.globalAlpha = 0.15 + v * 0.7;
        ctx.lineWidth = 4 + v * 10;
        ctx.beginPath(); ctx.moveTo(i * gap + gap / 2, 0); ctx.lineTo(i * gap + gap / 2, size); ctx.stroke();
    }

    ctx.shadowColor = conduitColor;                              // canvas glow — cheaper and tighter than stacking radial gradients
    ctx.fillStyle = nodeColor;
    for (let j = 0; j < lanes; j++) {
        for (let i = 0; i < lanes; i++) {
            const n = hash01(i + seed, j + seed);
            if (n < 0.35) continue;
            ctx.globalAlpha = 0.25 + n * 0.6;
            ctx.shadowBlur = 20 + n * 40;
            ctx.beginPath();
            ctx.arc(i * gap + gap / 2, j * gap + gap / 2, 6 + n * 12, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

// A CRAFT SURFACE — a bench or a machine — seen as a fabricated steel box with a recessed control panel set into
// the face. Every other generator here is a MATERIAL (grain, lattice, flow); this one is an OBJECT, so it fakes
// relief rather than substance. It does that by lighting one consistent direction throughout: the outer frame is
// RAISED (bright top-left edge, dark bottom-right), while the panel well is RECESSED — the same bevel inverted —
// so the two read as opposite depths against each other.
//
// The panel is not decoration. system-view raycasts the aimed cell and opens the crafting modal only when the hit
// lands inside it, so the drawn rect and the hit rect must be the same rect: both derive from CRAFT_PANEL_HALF.
// Tiles: the frame is drawn inset from all four edges and the brushed streaks use whole-cycle sines, so the seams
// stay quiet under RepeatWrapping.
//
// The panel is CENTRED deliberately. A centred square is invariant under a V flip, so the hit test never has to
// know whether the renderer's V axis runs up or down — a whole class of off-by-a-flip bug that simply cannot occur.
export const CRAFT_PANEL_HALF = 0.22;   // half-extent from the face centre, in UV → panel spans 0.28 … 0.72

function createCraftSurfaceTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    // This file's lighten/darken are ADDITIVE (±amount*255 per channel) and return "rgb(…)", while blendColors
    // takes (rgb-string, #hex) IN THAT ORDER. Mixing those up returns "rgb(NaN,…)", which canvas silently ignores
    // on fillStyle — the fill keeps the previous colour instead of failing. Scaling multiplicatively is predictable
    // and preserves hue, so everything below goes through mul().
    const mul = (hex, f) => {
        const n = parseInt(hex.slice(1), 16);
        const c = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
        return `rgb(${c(n >> 16)}, ${c((n >> 8) & 255)}, ${c(n & 255)})`;
    };
    const steel = mul(baseHex, 1.0);
    const lit = mul(baseHex, 1.34);
    const shade = mul(baseHex, 0.70);
    const deep = mul(baseHex, 0.44);
    const black = mul(baseHex, 0.20);
    const GLOW = '#6FD8FF';        // panel screen — cool, so it reads as lit rather than as painted-on colour
    const WARN = '#FFB347';

    ctx.fillStyle = steel;
    ctx.fillRect(0, 0, size, size);

    // Brushed finish: dense, very low-contrast horizontal streaks. Whole cycles only so the left edge meets the right.
    for (let i = 0; i < 300; i++) {
        const y = (i / 300) * size;
        const cycles = 1 + Math.floor(hash01(i + seed, 1) * 3);
        const amp = 0.6 + hash01(i + seed, 2) * 2.2;
        ctx.globalAlpha = 0.04 + hash01(i, seed + 3) * 0.07;
        ctx.strokeStyle = hash01(i, seed + 4) > 0.5 ? lit : shade;
        ctx.lineWidth = 0.7 + hash01(i, seed + 5) * 1.6;
        ctx.beginPath();
        for (let x = 0; x <= size; x += 32) {
            const yy = y + Math.sin((x / size) * Math.PI * 2 * cycles + hash01(i + seed, 6) * 6.283) * amp;
            if (x === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
        }
        ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // RAISED outer frame, inset from the tile edge so neighbouring cells don't fuse into one slab.
    const fi = size * 0.045, fw = size * 0.055;
    ctx.fillStyle = mul(baseHex, 1.08);
    ctx.fillRect(fi, fi, size - 2 * fi, fw);                       // top rail
    ctx.fillRect(fi, size - fi - fw, size - 2 * fi, fw);           // bottom rail
    ctx.fillRect(fi, fi, fw, size - 2 * fi);                       // left stile
    ctx.fillRect(size - fi - fw, fi, fw, size - 2 * fi);           // right stile
    ctx.fillStyle = lit;  ctx.globalAlpha = 0.55;
    ctx.fillRect(fi, fi, size - 2 * fi, 5);                        // lit top edge
    ctx.fillRect(fi, fi, 5, size - 2 * fi);                        // lit left edge
    ctx.fillStyle = deep; ctx.globalAlpha = 0.55;
    ctx.fillRect(fi, size - fi - 5, size - 2 * fi, 5);             // shadowed bottom edge
    ctx.fillRect(size - fi - 5, fi, 5, size - 2 * fi);             // shadowed right edge
    ctx.globalAlpha = 1;

    // Corner bolts: dome shading, highlight up-left, contact shadow down-right — same light as everything else.
    const bolt = (cx, cy, rad) => {
        ctx.fillStyle = deep; ctx.globalAlpha = 0.5;
        ctx.beginPath(); ctx.arc(cx + rad * 0.24, cy + rad * 0.24, rad, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
        const g = ctx.createRadialGradient(cx - rad * 0.4, cy - rad * 0.4, rad * 0.1, cx, cy, rad);
        g.addColorStop(0, blendColors(lit, '#FFFFFF', 0.45));
        g.addColorStop(0.55, steel);
        g.addColorStop(1, deep);
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(cx, cy, rad, 0, Math.PI * 2); ctx.fill();
    };
    const bo = fi + fw / 2, br = size * 0.017;
    bolt(bo, bo, br); bolt(size - bo, bo, br); bolt(bo, size - bo, br); bolt(size - bo, size - bo, br);

    // ---- The control panel. Drawn from CRAFT_PANEL_HALF so it is literally the rect the hit test uses. ----
    const p0 = (0.5 - CRAFT_PANEL_HALF) * size, pw = CRAFT_PANEL_HALF * 2 * size;

    ctx.fillStyle = deep; ctx.globalAlpha = 0.45;                  // ambient occlusion pooled around the well
    ctx.fillRect(p0 - 10, p0 - 10, pw + 20, pw + 20);
    ctx.globalAlpha = 1;
    ctx.fillStyle = black;
    ctx.fillRect(p0, p0, pw, pw);
    // RECESSED bevel = the frame's bevel inverted: dark where the frame was bright.
    ctx.fillStyle = deep; ctx.globalAlpha = 0.85;
    ctx.fillRect(p0, p0, pw, 7); ctx.fillRect(p0, p0, 7, pw);
    ctx.fillStyle = lit;  ctx.globalAlpha = 0.40;
    ctx.fillRect(p0, p0 + pw - 6, pw, 6); ctx.fillRect(p0 + pw - 6, p0, 6, pw);
    ctx.globalAlpha = 1;

    // Screen: a 2×2 crafting grid, lit. Reads as "combine things here" at a glance and survives being small.
    const sm = pw * 0.16, sx = p0 + sm, sw = pw - 2 * sm;
    ctx.fillStyle = mul(baseHex, 0.13);
    ctx.fillRect(sx, p0 + sm, sw, sw);
    ctx.strokeStyle = GLOW; ctx.globalAlpha = 0.30; ctx.lineWidth = 3;
    ctx.strokeRect(sx, p0 + sm, sw, sw);
    ctx.globalAlpha = 1;

    const cellGap = sw * 0.10, cell = (sw - 3 * cellGap) / 2;
    for (let r = 0; r < 2; r++) for (let c = 0; c < 2; c++) {
        const x = sx + cellGap + c * (cell + cellGap), y = p0 + sm + cellGap + r * (cell + cellGap);
        const on = hash01(r * 2 + c + seed, 9) > 0.35;             // a couple of cells dark → it reads as in-use
        ctx.fillStyle = GLOW; ctx.globalAlpha = on ? 0.55 : 0.12;
        ctx.fillRect(x, y, cell, cell);
        ctx.globalAlpha = on ? 0.95 : 0.30; ctx.strokeStyle = GLOW; ctx.lineWidth = 2.5;
        ctx.strokeRect(x, y, cell, cell);
    }
    ctx.globalAlpha = 1;

    // Indicator LEDs along the bottom lip of the well, below the screen.
    const ly = p0 + pw - sm * 0.55;
    [GLOW, GLOW, WARN].forEach((col, i) => {
        const lx = sx + sw * (0.22 + i * 0.28), lr = pw * 0.022;
        const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, lr * 2.6);
        g.addColorStop(0, col); g.addColorStop(0.35, col); g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalAlpha = 0.85; ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(lx, ly, lr * 2.6, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1; ctx.fillStyle = col;
        ctx.beginPath(); ctx.arc(lx, ly, lr, 0, Math.PI * 2); ctx.fill();
    });

    // Wear: shallow scuffs on the metal, the only thing here off the grid.
    ctx.lineCap = 'round';
    for (let s = 0; s < 26; s++) {
        const x0 = hash01(s + seed, 11) * size, y0 = hash01(s + seed, 12) * size;
        if (x0 > p0 - 20 && x0 < p0 + pw + 20 && y0 > p0 - 20 && y0 < p0 + pw + 20) continue;   // keep the panel clean
        const a = hash01(s + seed, 13) * Math.PI, len = 14 + hash01(s + seed, 14) * 70;
        ctx.globalAlpha = 0.07 + hash01(s, seed + 15) * 0.13;
        ctx.strokeStyle = hash01(s, seed + 16) > 0.5 ? deep : lit;
        ctx.lineWidth = 1 + hash01(s, seed + 17) * 1.8;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x0 + Math.cos(a) * len, y0 + Math.sin(a) * len); ctx.stroke();
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

// THE CRUST — the Mineral and Regolith slots, thirty-two kubes each. The two used to be one right-triangle stipple at
// two scales, which is why a matched pair (Arid Mineral and Arid Regolith share every property but the name) could not
// be told apart on the ground. texture-crust.js draws them now, as crystalline rock and as loose soil, from fixed seeds
// and never from a hex; it is THREE-free so doc/world-generation.html draws its preview with the same code. Anything
// else that reaches this function (no current slot does) keeps the plain white canvas it always got.
function createCrustTexture(type) {
    let canvas;
    if (type === 'mineral') canvas = drawMineralCrust();
    else if (type === 'regolith') canvas = drawRegolithCrust();
    else {
        canvas = document.createElement('canvas');
        canvas.width = canvas.height = 1024;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 1024, 1024);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

// A BIOLITH PATTERN (texture-biolith.js): the maker's grey canvas, as a tiling texture. The kube's hex is the
// material's colour, multiplied in on the GPU, so all thirty-two pigments of a pattern share this one canvas.
function biolithTexture(pattern) {
    const texture = new THREE.CanvasTexture(BIOLITH_MAKERS[pattern]());
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

// A BUILDING KUBE'S FACE (texture-building.js), the same way: one grey canvas per style, tinted by material.color, so
// every colour of an idea shares it. The Steel Grate's canvas is transparent between its bars (the alpha is the holes).
function buildingTexture(style) {
    const texture = new THREE.CanvasTexture(BUILD_MAKERS[style]());
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

const textureCache = {
    mineral: null,
    regolith: null,
    iron: null,
    gold: null,
    copper: null,
    silver: null,
    pyrite: null,
    magnetite: null,
    phosphorus: null,
    sulfur: null,
    // One slot per metal that used to fall back to Iron's (texture-metals.js). Each bakes a canvas that depends on
    // nothing but its own name, so no metal can change how another looks by being drawn first.
    aluminum: null, calcium: null, sodium: null, manganese: null, titanium: null, chromium: null,
    magnesium: null, cobalt: null, zirconium: null, steel: null, structuralalloy: null,
    siliconcarbide: null,
    graphene: null,
    brass: null,
    inconel: null,
    sulfuricacid: null,
    heavywater: null,
    liquidargon: null,
    superconductor: null,
    hypercor: null,
    exoticmatter: null,
    fhide: null, fbone: null, fshell: null, ffibre: null, feggs: null, fscales: null, fgland: null, fvenom: null, ffeather: null, fcrystal: null, ftholin: null,   // (alien animals, Oct 28; Feathers Oct 29; Crystal Shard and Tholin Gel Oct 30)
    // One slot per CRAFT SURFACE. They share a generator but not a cache, so each bakes from its own hex instead of
    // wearing whichever sibling rendered first — the invariant the crafted block below documents.
    benchi: null,
    smelter: null,
    electrolyzer: null,
    chemsynth: null,
    quantumasm: null,
    // ── the material library (texture-materials.js). One slot per look; `glass` and `gasvessel` are SHARED by 32
    // panes and 11 gases respectively, which is why those two generators are neutral and seed-stable.
    glass: null, wiredglass: null,
    silicon: null, carbon: null, alkali: null, halite: null, bauxite: null,
    titaniumore: null, slag: null, aerogel: null, cor: null,
    waterice: null, co2ice: null, methaneice: null, ammoniaice: null, nitrogenice: null, tholin: null,
    circuit: null, photovoltaic: null, powercore: null, armorplate: null, silica: null,
    gasvessel: null, cryolox: null, cryolin: null, cryolch4: null, cryolcl: null, cryolco2: null, cryolnh3: null, waterflow: null
};

// Kube textures are viewed at extreme grazing angles on a cube world's limb, where isotropic filtering has to pick
// the mip for the LARGER derivative — over-blurring one axis while still aliasing the other. Anisotropy fixes that,
// but three defaults it to 1 and it was never raised. The renderer owner calls setMaxAnisotropy() once at init;
// until then this stays 1, which is exactly today's behaviour.
let _maxAniso = 1;
// EVERY LOOK THE ENGINE CAN BAKE, BY NAME (the owner, Sep 30: a review of every material's look, with alternatives to
// choose from). The cache slots below are the answer to "what does this material wear today"; this is the answer to
// "what COULD it wear": each generator under a style id, with the family it belongs to (what kind of substance it
// draws), a label for the review page, and a maker that bakes it in a given hex. applyMaterial does not read this yet;
// the review page does, and the picks will be applied through it.
const STYLE_FAMILIES = ['rock', 'metal', 'glass', 'ice', 'liquid', 'gas', 'tech', 'life'];
export const TEXTURE_STYLES = new Map([
    ['mineral',        { family: 'rock',   label: 'Mineral stipple',      make: () => createCrustTexture('mineral') }],
    ['regolith',       { family: 'rock',   label: 'Regolith grain',       make: () => createCrustTexture('regolith') }],
    ['silicon',        { family: 'rock',   label: 'Conchoidal fracture',  make: createSiliconFractureTexture }],
    ['halite',         { family: 'rock',   label: 'Cubic cleavage',       make: createHaliteCleavageTexture }],
    ['bauxite',        { family: 'rock',   label: 'Pisolitic nodules',    make: createPisoliticTexture }],
    ['titaniumore',    { family: 'rock',   label: 'Disseminated ore',     make: createDisseminatedOreTexture }],
    ['slag',           { family: 'rock',   label: 'Vesicular slag',       make: createVesicularSlagTexture }],
    ['silica',         { family: 'rock',   label: 'Silica sand',          make: createSilicaSandTexture }],
    ['tholin',         { family: 'rock',   label: 'Tholin crust',         make: createTholinCrust }],
    ['cor',            { family: 'rock',   label: 'Cor veins',            make: createCorTexture }],
    ['carbon',         { family: 'rock',   label: 'Graphite plates',      make: createGraphitePlateTexture }],
    ['alkali',         { family: 'rock',   label: 'Alkali tarnish',       make: createAlkaliTarnishTexture }],
    ['crystalline',    { family: 'rock',   label: 'Crystal lattice',      make: createCrystallineLattice }],
    ['iron',           { family: 'metal',  label: 'Iron lattice',         make: createIronLattice }],
    ['gold',           { family: 'metal',  label: 'Gold lattice',         make: createGoldLattice }],
    ['copper',         { family: 'metal',  label: 'Copper lattice',       make: createCopperLattice }],
    ['silver',         { family: 'metal',  label: 'Silver lattice',       make: createSilverLattice }],
    ['pyrite',         { family: 'metal',  label: 'Pyrite cubes',         make: createPyriteLattice }],
    ['magnetite',      { family: 'metal',  label: 'Magnetite grains',     make: createMagnetiteLattice }],
    ['phosphorus',     { family: 'metal',  label: 'Phosphorus waxy',      make: createPhosphorusLattice }],
    ['sulfur',         { family: 'metal',  label: 'Sulfur crystals',      make: createSulfurLattice }],
    ...[...METAL_SURFACES].map(([k, make]) => [k, { family: 'metal', label: k === 'structuralalloy' ? 'Structural alloy' : k[0].toUpperCase() + k.slice(1) + ' surface', make }]),
    ['damascus',       { family: 'metal',  label: 'Damascus folds',       make: createDamascusPattern }],
    ['armorplate',     { family: 'metal',  label: 'Armour plate',         make: createArmorPlateTexture }],
    ['glass',          { family: 'glass',  label: 'Float glass',          make: createFloatGlassTexture }],
    ['wiredglass',     { family: 'glass',  label: 'Wired glass',          make: createWiredGlassTexture }],
    ['aerogel',        { family: 'glass',  label: 'Nanofoam',             make: createNanofoamTexture }],
    ['waterice',       { family: 'ice',    label: 'Glacial ice',          make: createGlacialIce }],
    ['co2ice',         { family: 'ice',    label: 'Dry-ice frost',        make: createDryIceFrost }],
    ['methaneice',     { family: 'ice',    label: 'Bladed frost',         make: createBladedFrost }],
    ['ammoniaice',     { family: 'ice',    label: 'Sugar frost',          make: createSugarFrost }],
    ['nitrogenice',    { family: 'ice',    label: 'Convection cells',     make: createConvectionIce }],
    ['waterflow',      { family: 'liquid', label: 'Flowing liquid',       make: createFluidFlowTexture }],
    ['cryoboil',       { family: 'liquid', label: 'Cryogenic boil',       make: createCryoBoilTexture }],
    ['gasvessel',      { family: 'gas',    label: 'Gas vessel',           make: createGasVesselTexture }],
    ['circuit',        { family: 'tech',   label: 'Circuit board',        make: createCircuitBoardTexture }],
    ['photovoltaic',   { family: 'tech',   label: 'Photovoltaic cells',   make: createPhotovoltaicTexture }],
    ['powercore',      { family: 'tech',   label: 'Power core',           make: createPowerCoreTexture }],
    ['battery',        { family: 'tech',   label: 'Battery gauge',        make: (hex) => createBatteryTexture(hex, 1) }],
    ['craftsurface',   { family: 'tech',   label: 'Craft surface',        make: createCraftSurfaceTexture }],
    ['emissive',       { family: 'tech',   label: 'Emissive pulse',       make: createEmissivePulseLattice }],
    ['flat',           { family: 'life',   label: 'Flat colour',          make: () => createCrustTexture('none') }],   // what life's materials wear today
    ...ORGANIC_STYLES.map(([k, label, make]) => [k, { family: 'life', label, make }]),
]);
export { STYLE_FAMILIES };
// The style a cache slot bakes today (applyMaterial's chain, as data): the review's "original" for each look.
export function styleOfSlot(slot) {
    if (TEXTURE_STYLES.has(slot)) return slot;
    if (slot === 'siliconcarbide' || slot === 'graphene') return 'crystalline';
    if (slot === 'brass' || slot === 'inconel' || slot === 'fhide' || slot === 'ffibre' || slot === 'ffeather') return 'damascus';
    if (slot === 'fbone' || slot === 'fshell' || slot === 'feggs' || slot === 'fscales') return 'crystalline';
    if (slot === 'fvenom') return 'waterflow';
    if (slot === 'fgland') return 'emissive';
    if (slot === 'sulfuricacid' || slot === 'heavywater' || slot === 'liquidargon') return 'waterflow';
    if (slot === 'superconductor' || slot === 'hypercor' || slot === 'exoticmatter') return 'emissive';
    if (slot === 'solarpanel') return 'photovoltaic';
    if (/^cryol/.test(slot)) return 'cryoboil';
    if (isCraftSurfaceKey(slot)) return 'craftsurface';
    return null;
}

export function setMaxAnisotropy(n) {
    const v = Math.max(1, Math.floor(n || 1));
    if (v === _maxAniso) return;
    _maxAniso = v;
    for (const k in textureCache) { const t = textureCache[k]; if (t) { t.anisotropy = v; t.needsUpdate = true; } }
}
// A TEXTURE SHARED BETWEEN RENDERERS HOLDS ON TO EVERY ONE OF THEM (STAB-05). three.js hangs a 'dispose' listener on
// a texture for each renderer that uploads it, and that listener closes over the renderer's GL context, whose canvas
// carries the listeners of the view that drew it. These textures outlive every view, so each system view a player had
// entered stayed reachable through them after it was torn down: measured, one kube texture carried 8 such listeners
// after 7 trips (the icon renderer's, and one per system view). The system view calls this as it unmounts. dispose()
// drops every renderer's GPU copy and its listener; the texture itself stays usable, and uploads again the next time
// anything draws with it (the icon renderer rarely does: finished icons are cached as pictures).
export function releaseSharedTextures() {
    for (const k in textureCache) { const t = textureCache[k]; if (t) t.dispose(); }
}
// WHICH TEXTURE A KUBE WEARS lives in texture-dispatch.js — the same decision, but with no THREE import, so a node
// test can walk the real catalogues and hold the "no kube renders as a flat colour" promise. Re-exported here so
// every existing caller (system-view.js's voxel crust, app.js's hotbar icon, doc/kube-textures.html) is unchanged.
export { kubeTextureType, craftedTextureKey, nativeTextureKey, isCraftSurfaceName, bitBaseName, metalnessFor, iconMetalness, CRAFTED_TEXTURES, CRAFT_SURFACE_KEYS };

// ── SHIPPING RESOLUTION ──────────────────────────────────────────────────────────────────────────────────────────
// Every generator DRAWS at 1024 — that is the resolution its feature sizes and line widths were tuned at, and
// dropping it would mean re-tuning 25 generators. But a 1024 RGBA canvas with mipmaps costs ~5.6 MB of GPU memory,
// and the codex warms an icon for every recipe ingredient at idle, so all of them end up resident at once. Measured
// on the real catalogue: 25 canvases = 134 MB before this work, 53 = 284 MB after. This codebase still targets an
// iPad Air 2, so that is not a trade we can make silently.
//
// So: draw at 1024, then downsample once to the size the material actually needs. The browser's own image scaler
// does a correct box filter, the generator is untouched, and a fine-grained material loses nothing a player can see
// at the size a kube face is ever drawn. Only the NEW slots are capped — the 25 that shipped before keep their
// exact pixels, because changing an established look to save memory is a different decision from choosing one.
const TEX_PX = {
    // Detail that a player reads as INFORMATION rather than as surface — traces, pads, busbars — stays at full size.
    circuit: 1024, photovoltaic: 1024,
    // Everything else is a surface. 512 is a 4x saving and none of these carry structure that survives being read
    // at a kube face's on-screen size anyway.
    glass: 512, wiredglass: 512,
    silicon: 512, carbon: 512, alkali: 512, halite: 512, bauxite: 512,
    titaniumore: 512, slag: 512, aerogel: 512, cor: 512,
    waterice: 512, co2ice: 512, methaneice: 512, ammoniaice: 512, nitrogenice: 512, tholin: 512,
    powercore: 512, armorplate: 512, silica: 512,
    // The eleven metals that each got a slot of their own. They were one 1024 canvas between them (Iron's); at 512 they
    // are eleven quarter-size canvases, about 15 MB of GPU memory with mipmaps if every one is resident at once.
    aluminum: 512, calcium: 512, sodium: 512, manganese: 512, titanium: 512, chromium: 512,
    magnesium: 512, cobalt: 512, zirconium: 512, steel: 512, structuralalloy: 512,
    // …and the battery, which is baked once per charge step — at 1024 a full ladder would be 36MB of canvas for a
    // gauge whose largest feature is a ring.
    battery: 512,
    // The softest of the lot — a gas is a haze and a cryogen is a bubble field; neither has an edge to lose.
    gasvessel: 256, cryolox: 256, cryolin: 256, cryolch4: 256, cryolcl: 256, cryolco2: 256, cryolnh3: 256, waterflow: 256
};
// THE BIOLITH PATTERNS ship at 512 like every slot added after the first twenty-five: nine 'biolith:<pattern>' slots
// (one per pattern, whatever the pigment), so their size is a rule on the prefix rather than nine entries above.
const shippingPx = (key) => TEX_PX[key] || (/^(biolith|build):/.test(key || '') ? 512 : undefined);   // the building kubes' 'build:<style>' slots too
function atShippingSize(tex, key) {
    const px = shippingPx(key);
    const img = tex && tex.image;
    if (!px || !img || img.width <= px) return tex;
    const c = document.createElement('canvas');
    c.width = c.height = px;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, px, px);
    const out = new THREE.CanvasTexture(c);
    out.wrapS = out.wrapT = THREE.RepeatWrapping;   // the whole point of these tiles — must survive the resize
    tex.dispose();
    return out;
}

// `fill` (0..1) is read only by the BATTERY, whose face is a charge gauge. It is quantised here rather than by the
// caller so every call site lands on the same ladder of cache slots — a continuous value would bake a fresh 512²
// canvas on every tick of the energy clock.
// Does this kube's face carry an instrument rather than a material? The icon renderer asks, because a gauge must
// be shown WHOLE where a material is shown zoomed. Resolved through the same two dispatch tables applyMaterial uses,
// so the answer cannot drift from which texture actually gets baked.
// …AND A CARVED KUBE (biolith.mjs, Oct 13): its face is one framed motif, not a running pattern, so its icon shows the
// whole of it too (zoomed, it would be a corner of a frame and a stroke of a hexagon).
export function isGaugeKube(name) {
  const n = bitBaseName(name || '') || name || '';
  if (biolithTextureKey(name || '') === 'biolith:glyph') return true;
  const bk = buildingTextureKey(name || '');                       // a building kube's panel, lamp, cut or column (BUILDING_WHOLE_FACE)
  if (bk) return BUILDING_WHOLE_FACE.has(bk.slice(6));
  return isGaugeKey(craftedTextureKey(name || '') || nativeTextureKey(n) || '');
}
// A PILLAR'S PART (building-kubes.mjs pillarRole, Oct 13). The world draws a placed Mineral Pillar in the canvas of the
// part it is in its column (base, shaft, capital, or the short column whole), which is not a function of its name, so the
// renderer asks for the part's canvas by style here. The same cache, the same shipping size and the same tint as
// applyMaterial gives a building kube.
export function applyBuildingTexture(meshOrMaterial, style, hexColor) {
    const key = 'build:' + style;
    if (!BUILD_MAKERS[style]) return;
    if (!textureCache[key]) textureCache[key] = atShippingSize(buildingTexture(style), key);
    const tex = textureCache[key];
    if (tex && tex.anisotropy !== _maxAniso) { tex.anisotropy = _maxAniso; tex.needsUpdate = true; }
    const material = meshOrMaterial.material || meshOrMaterial;
    if (material) { material.color.set(hexColor); material.map = tex; material.needsUpdate = true; }
}
export const BATTERY_FILL_STEPS = 8;
export function batteryFillStep(fill) {
  const f = Math.max(0, Math.min(1, Number(fill)));
  return Number.isFinite(f) ? Math.round(f * BATTERY_FILL_STEPS) : BATTERY_FILL_STEPS;
}
// THE PICKED TEXTURES (texture-picks.mjs): a slot the owner picked a recipe for bakes that recipe (texture-recipes.js)
// instead of its old generator, once, shared by every member in its own colour; life's materials, flat until now, wear
// their family's. Baked at 1024 and shipped at its slot's size (TEX_PX), 512 at most but for the two whose detail is
// information.
const LIFE_FAMILY = new Map(Object.values(ORGANIC_MATERIALS).map((m) => [m.name, m.family]));
setLifeFamilyLookup((name) => LIFE_FAMILY.get(name) || (/^[A-Z][a-z]+ Turf Bit$/.test(name || '') ? 'leaf' : name === 'Dead Coral' ? 'coral' : null));   // …and Dead Coral (reservoirs.mjs) the coral's, grey   // a turf bit (version 9) wears the leaves' texture, in its own colour
const RECIPE_BY_ID = new Map(Object.values(LOOK_TEXTURES).flat().map((r) => [r.id, r]));
const _recipeTex = new Map();
function bakeRecipeCanvas(r, px) {
    let c = bakeRecipe(r);
    if (c.width > px) { const d = document.createElement('canvas'); d.width = d.height = px; const x = d.getContext('2d'); x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high'; x.drawImage(c, 0, 0, px, px); c = d; }
    return c;
}
function recipeTexture(id, key) {
    let t = _recipeTex.get(id);
    if (!t) {
        const r = RECIPE_BY_ID.get(id); if (!r) return null;
        const px = Math.min(TEX_PX[key] || 512, key === 'circuit' || key === 'photovoltaic' ? 1024 : 512);   // straight to its shipping size: one resample
        const stand = _bakeDefer > 0 ? recipeStandIn(id, r, px) : null;   // a world build: baked by the worker, a flat grey meanwhile
        t = new THREE.CanvasTexture(stand || bakeRecipeCanvas(r, px));
        t.wrapS = t.wrapT = THREE.RepeatWrapping;
        _recipeTex.set(id, t);
        if (stand) { _bakePending.get(id).t = t; t.userData.recipePending = id; }
    } else if (_bakePending.has(id) && _bakeDefer === 0) finishRecipeNow(id);   // anyone who cannot wait (an icon) gets it baked now
    return t;
}
// ── RECIPES BAKED OFF THE MAIN THREAD (launch test A2, Oct 7) ──────────────────────────────────────────────────────
// Baking a world's recipes was most of its first build (0.8 s of 0.95 s on a Ø72 world, all on the main thread, while
// the loading screen waited). Inside deferRecipeBakes (system-view.js's world build) a recipe not yet baked is sent to
// recipe-worker.js, and its texture holds a small tile of the recipe's own mean grey (what the level step centres every
// recipe on), so the kube shows its colour at its usual brightness meanwhile. When the worker answers, the pixels go
// onto a canvas and the texture's image is swapped: every material sharing it updates at once, nothing is rebuilt.
// Anything that asks outside a world build (an icon, a Kubipedia swatch, a share picture) gets the recipe baked on the
// spot if it is still pending (finishRecipeNow), and flushRecipeBakes does that for all of them. With no Worker or no
// 2D OffscreenCanvas (node, Safari before 16.4), everything bakes on the main thread exactly as before.
let _bakeDefer = 0, _bakeWorker = null, _bakeWorkerDead = false;
const _bakePending = new Map();   // id → { r, px, t }
export function deferRecipeBakes(fn) { _bakeDefer++; try { return fn(); } finally { _bakeDefer--; } }
export function recipeBakesPending() { return _bakePending.size; }
export function flushRecipeBakes() { for (const id of [..._bakePending.keys()]) finishRecipeNow(id); }
// A kube's texture, baked ahead in the worker for a caller that can wait (app.js's codex icon warm-up): true while it
// is still baking. A slot with no picked recipe has nothing to bake ahead, and answers false.
export function prefetchKubeTexture(type, metalName) {
    if (!type) return false;
    const key = textureSlot(type, metalName), id = pickedRecipeFor(key);
    if (!id) return false;
    deferRecipeBakes(() => recipeTexture(id, key));
    return _bakePending.has(id);
}
// …and a family of life's texture (lifeTexture), for an icon that draws life as the world does (app.js drawWorldIcon: a
// flora's or a seedling's micro-cubes): true while it is still baking.
export function prefetchLifeTexture(family) {
    const id = family ? LIFE_PICKS[family] : null;
    if (!id) return false;
    deferRecipeBakes(() => recipeTexture(id, 'life:' + family));
    return _bakePending.has(id);
}
// …and one texture, for a caller about to read its pixels (system-view.js's look histogram)
export function ensureTextureBaked(tex) { const id = tex && tex.userData && tex.userData.recipePending; if (id) finishRecipeNow(id); return tex; }
function bakeWorker() {
    if (_bakeWorker || _bakeWorkerDead) return _bakeWorker;
    try {
        if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined' || typeof document === 'undefined') throw new Error('no worker');
        const u = new URL('./recipe-worker.js', import.meta.url); u.search = new URL(import.meta.url).search;   // the same build's modules
        _bakeWorker = new Worker(u, { type: 'module' });
        _bakeWorker.onmessage = (e) => {
            const { id, w, data, error } = e.data || {};
            const p = _bakePending.get(id); if (!p) return;              // baked on the spot meanwhile
            if (error || !data) { if (/OffscreenCanvas/.test(error || '')) _bakeWorkerDead = true; finishRecipeNow(id); return; }
            _bakePending.delete(id);
            try { const c = document.createElement('canvas'); c.width = c.height = w; c.getContext('2d').putImageData(new ImageData(data, w, w), 0, 0); swapRecipeImage(p.t, c); }
            catch (err) { _bakePending.set(id, p); finishRecipeNow(id); }
        };
        _bakeWorker.onerror = () => { _bakeWorkerDead = true; _bakeWorker = null; flushRecipeBakes(); };
    } catch (e) { _bakeWorkerDead = true; _bakeWorker = null; }
    return _bakeWorker;
}
function recipeStandIn(id, r, px) {
    const wk = bakeWorker(); if (!wk) return null;
    const c = document.createElement('canvas'); c.width = c.height = 4;
    const ctx = c.getContext('2d'), g = Math.max(0, Math.min(255, Math.round(r.mean === false ? (r.ground ?? 190) : (r.mean || 180))));
    ctx.fillStyle = `rgb(${g},${g},${g})`; ctx.fillRect(0, 0, 4, 4);
    _bakePending.set(id, { r, px, t: null });
    try { wk.postMessage({ id, recipe: r, px }); } catch (e) { _bakePending.delete(id); return null; }
    return c;
}
function finishRecipeNow(id) {
    const p = _bakePending.get(id); if (!p) return;
    _bakePending.delete(id);
    if (p.t) swapRecipeImage(p.t, bakeRecipeCanvas(p.r, p.px));
}
function swapRecipeImage(t, c) {
    t.image = c;
    delete t.userData.recipePending;
    // a new size needs a new GPU texture: three keeps the old storage for a same-texture re-upload, so drop it first
    try { t.dispose(); } catch (e) {}
    t.needsUpdate = true;
}
export function pickedRecipeFor(textureKey) {
    return KUBE_PICKS[textureKey] || (textureKey && textureKey.startsWith('life:') ? LIFE_PICKS[textureKey.slice(5)] : null) || null;
}
// What a kube's texture is called (Kubipedia's "Texture"): the picked recipe's label, else its kind, else null (flat).
export function textureLabelFor(name, metalness) {
    const type = kubeTextureType(name, metalness); if (!type) return null;
    if (type === 'biolith') { const p = biolithPattern(textureSlot(type, name).slice(8)); return p ? p.label.toLowerCase() + ' masonry' : type; }   // "running bond masonry"
    if (type === 'building') return BUILD_LABELS[textureSlot(type, name).slice(6)] || type;   // "chiselled masonry"
    const id = pickedRecipeFor(textureSlot(type, name)), r = id && RECIPE_BY_ID.get(id);
    return r ? r.label : type;
}
// The texture a family of life's materials wears (null if none was picked), for the shared bit buckets that colour each
// bit by instance (system-view.js 'b:~life:<family>').
export function lifeTexture(family) {
    const id = LIFE_PICKS[family]; if (!id) return null;
    const t = recipeTexture(id, 'life:' + family);
    if (t && t.anisotropy !== _maxAniso) { t.anisotropy = _maxAniso; t.needsUpdate = true; }
    return t;
}

export function applyMaterial(meshOrMaterial, type = 'mineral', hexColor = '#ffffff', metalName = null, fill = 1) {
    // WHICH SLOT — the metal lattices, the native and crafted names, and the two fallbacks — is textureSlot's answer
    // (texture-dispatch.js). It lives there so the catalogue page and its node test can ask the same question this
    // does; the rules and their order are unchanged from when they were written out inline here.
    const textureKey = textureSlot(type, metalName);

    // A battery keeps ONE SLOT PER CHARGE STEP. Everything else is a pure function of its hex and caches once; this
    // one is a function of hex AND charge, so the slot has to say which charge it is or the first battery a player
    // ever holds would freeze its texture at whatever it was charged to.
    const step = textureKey === 'battery' ? batteryFillStep(fill) : null;
    const cacheKey = step == null ? textureKey : (textureKey + '@' + step);

    const picked = pickedRecipeFor(textureKey);
    if (picked && !textureCache[cacheKey]) textureCache[cacheKey] = recipeTexture(picked, textureKey);
    if (!textureCache[cacheKey]) {
        if (textureKey === 'iron') textureCache[cacheKey] = createIronLattice(hexColor);
        else if (textureKey === 'gold') textureCache[cacheKey] = createGoldLattice(hexColor);
        else if (textureKey === 'copper') textureCache[cacheKey] = createCopperLattice(hexColor);
        else if (textureKey === 'silver') textureCache[cacheKey] = createSilverLattice(hexColor);
        else if (textureKey === 'pyrite') textureCache[cacheKey] = createPyriteLattice(hexColor);
        else if (textureKey === 'magnetite') textureCache[cacheKey] = createMagnetiteLattice(hexColor);
        else if (textureKey === 'phosphorus') textureCache[cacheKey] = createPhosphorusLattice(hexColor);
        else if (textureKey === 'sulfur') textureCache[cacheKey] = createSulfurLattice(hexColor);
        else if (METAL_SURFACES.has(textureKey)) textureCache[cacheKey] = METAL_SURFACES.get(textureKey)(hexColor);
        else if (textureKey === 'siliconcarbide' || textureKey === 'graphene') textureCache[cacheKey] = createCrystallineLattice(hexColor);
        else if (textureKey === 'brass' || textureKey === 'inconel' || textureKey === 'fhide' || textureKey === 'ffibre' || textureKey === 'ffeather') textureCache[cacheKey] = createDamascusPattern(hexColor);
        else if (textureKey === 'fbone' || textureKey === 'fshell' || textureKey === 'feggs' || textureKey === 'fscales') textureCache[cacheKey] = createCrystallineLattice(hexColor);
        else if (textureKey === 'fvenom' || textureKey === 'ftholin') textureCache[cacheKey] = createFluidFlowTexture(hexColor);
        else if (textureKey === 'fcrystal') textureCache[cacheKey] = createCrystallineLattice(hexColor);
        else if (textureKey === 'fgland') textureCache[cacheKey] = createEmissivePulseLattice(hexColor);
        else if (textureKey === 'sulfuricacid' || textureKey === 'heavywater' || textureKey === 'liquidargon') textureCache[cacheKey] = createFluidFlowTexture(hexColor);
        else if (textureKey === 'superconductor' || textureKey === 'hypercor' || textureKey === 'exoticmatter') textureCache[cacheKey] = createEmissivePulseLattice(hexColor);
        else if (textureKey === 'glass') textureCache[cacheKey] = createFloatGlassTexture(hexColor);
        else if (textureKey === 'wiredglass') textureCache[cacheKey] = createWiredGlassTexture(hexColor);
        else if (textureKey === 'silicon') textureCache[cacheKey] = createSiliconFractureTexture(hexColor);
        else if (textureKey === 'carbon') textureCache[cacheKey] = createGraphitePlateTexture(hexColor);
        else if (textureKey === 'alkali') textureCache[cacheKey] = createAlkaliTarnishTexture(hexColor);
        else if (textureKey === 'halite') textureCache[cacheKey] = createHaliteCleavageTexture(hexColor);
        else if (textureKey === 'bauxite') textureCache[cacheKey] = createPisoliticTexture(hexColor);
        else if (textureKey === 'titaniumore') textureCache[cacheKey] = createDisseminatedOreTexture(hexColor);
        else if (textureKey === 'slag') textureCache[cacheKey] = createVesicularSlagTexture(hexColor);
        else if (textureKey === 'aerogel') textureCache[cacheKey] = createNanofoamTexture(hexColor);
        else if (textureKey === 'cor') textureCache[cacheKey] = createCorTexture(hexColor);
        else if (textureKey === 'waterice') textureCache[cacheKey] = createGlacialIce(hexColor);
        else if (textureKey === 'co2ice') textureCache[cacheKey] = createDryIceFrost(hexColor);
        else if (textureKey === 'methaneice') textureCache[cacheKey] = createBladedFrost(hexColor);
        else if (textureKey === 'ammoniaice') textureCache[cacheKey] = createSugarFrost(hexColor);
        else if (textureKey === 'nitrogenice') textureCache[cacheKey] = createConvectionIce(hexColor);
        else if (textureKey === 'tholin') textureCache[cacheKey] = createTholinCrust(hexColor);
        else if (textureKey === 'circuit') textureCache[cacheKey] = createCircuitBoardTexture(hexColor);
        else if (textureKey === 'photovoltaic') textureCache[cacheKey] = createPhotovoltaicTexture(hexColor);
        else if (textureKey === 'solarpanel') textureCache[cacheKey] = createPhotovoltaicTexture(hexColor);   // its own slot, its own hex
        else if (textureKey === 'powercore') textureCache[cacheKey] = createPowerCoreTexture(hexColor);
        else if (textureKey === 'battery') textureCache[cacheKey] = createBatteryTexture(hexColor, step / BATTERY_FILL_STEPS);   // its own charge, on its own face
        else if (textureKey === 'armorplate') textureCache[cacheKey] = createArmorPlateTexture(hexColor);
        else if (textureKey === 'silica') textureCache[cacheKey] = createSilicaSandTexture(hexColor);
        else if (textureKey === 'gasvessel') textureCache[cacheKey] = createGasVesselTexture(hexColor);
        else if (textureKey === 'cryolox' || textureKey === 'cryolin' || textureKey === 'cryolch4' || textureKey === 'cryolcl'
                 || textureKey === 'cryolco2' || textureKey === 'cryolnh3')
            textureCache[cacheKey] = createCryoBoilTexture(hexColor);
        else if (textureKey === 'waterflow') textureCache[cacheKey] = createFluidFlowTexture(hexColor);
        else if (isCraftSurfaceKey(textureKey)) textureCache[cacheKey] = createCraftSurfaceTexture(hexColor);
        else if (/^biolith:/.test(textureKey) && BIOLITH_MAKERS[textureKey.slice(8)]) textureCache[cacheKey] = biolithTexture(textureKey.slice(8));
        else if (/^build:/.test(textureKey) && BUILD_MAKERS[textureKey.slice(6)]) textureCache[cacheKey] = buildingTexture(textureKey.slice(6));   // never the hex: one grey canvas per style   // never the hex: one grey canvas per pattern, tinted by material.color
        else textureCache[cacheKey] = createCrustTexture(textureKey);
    }

    textureCache[cacheKey] = atShippingSize(textureCache[cacheKey], textureKey);
    const tex = textureCache[cacheKey];
    if (_bakeDefer === 0 && tex && tex.userData && tex.userData.recipePending) finishRecipeNow(tex.userData.recipePending);   // outside a world build: the real pixels, now
    if (tex && tex.anisotropy !== _maxAniso) { tex.anisotropy = _maxAniso; tex.needsUpdate = true; }
    const material = meshOrMaterial.material || meshOrMaterial;

    if (material) {
        material.color.set(hexColor);
        material.map = tex;
        material.needsUpdate = true;
    }
}